/**
 * 63.19 (DECISIONS 332; ARCHITECTURE §5.38; GameConfig.vehicleImpact): a moving hull striking a person on foot. Called by
 * `stepDrive` (core/drive.ts) for each driving body after its move, with its pose this tick; off, never called — a hull
 * shoves people out unhurt (`outOfHulls`), as before.
 *
 * Each person on foot whose hip comes within its radius of the hull's parts, the hull closing on it at `minWalks` or more
 * along the contact's normal, takes the bike's crash rule on its Δv (constants VEHICLE_IMPACT: closing speed × a set
 * mass), as the new blow cause `impact` (the vest takes nothing). Above `throwWalks` of Δv it is thrown (the airborne
 * cause `struck`, whose landing takes no fall damage — the blow is counted here); below, the walk's shove moves it out.
 * Prone, a tracked body over `crushWalks` kills it; a wheeled one hits it without the throw. A friend of the driver's side
 * takes the blow × the friendly-fire share (130 (d)): at 0, untouched, as before. A pair is struck at most once a
 * `pairSeconds`. The hull loses its share of the speed; a bike that loses over `riderThrowWalks` throws its rider.
 * A kill of an enemy is the driver's.
 *
 * Deterministic: the people in player order, vector math and Math.sqrt only, no draws.
 */
import type { GameState, PlayerState, StructureState, Vec3 } from '../types';
import { VEHICLE_IMPACT, BIKE_DRIVE, PERSON_BODY_HEIGHT, PHYS_DT } from '../constants';
import { vecAdd, vecDot, vecLength, vecNormalize, vecScale, vecSub } from '../math';
import { personSetOf } from './scaleProfile';
import { drivenHullShape, personHip, primDistance, primNearest, type Prim } from './hits';
import { projectToTangent } from './surfaceWalk';
import { damageUnit, friendlyShare } from './damage';
import { dismountUnit } from './seats';
import { personPointAt } from './fineTerrain';
import { getPlayerWorldPosition } from './gameState';

export type ImpactEvent =
  | { kind: 'struck'; structureId: number; unitId: number; driverId: number | null; dv: number; damage: number; thrown: boolean; crushed: boolean; killed: boolean; killerId?: number }
  | { kind: 'bikeCrash'; structureId: number; unitId: number; speed: number; damage: number };

type Body = keyof typeof VEHICLE_IMPACT.mass;

/** The hull's mass (tonnes), or 0 for a body that has none (it strikes nobody). */
export function hullMass(type: StructureState['type']): number {
  return VEHICLE_IMPACT.mass[type as Body] ?? 0;
}

/** The person's Δv and the hull's loss (world units a second) for a closing speed `c` against a hull of mass `M`. */
export function impactDv(c: number, M: number): { person: number; hull: number } {
  const m = VEHICLE_IMPACT.personMass, e = VEHICLE_IMPACT.restitution;
  return { person: ((1 + e) * M * c) / (M + m), hull: ((1 + e) * m * c) / (M + m) };
}

/** Where a person's feet stand (off the fine ground at the person's scale). */
function feetOf(state: GameState, p: PlayerState): Vec3 {
  const ps = personSetOf(state);
  return ps.fineAmplitude > 0 ? personPointAt(state.planets[p.planetIndex], p.surfaceDir, ps, 0) : getPlayerWorldPosition(p, state.planets);
}

/** Thrown along `dir` across the ground at `speed`, biased up, as the cause `struck`. */
function thrown(state: GameState, p: PlayerState, dir: Vec3, speed: number): PlayerState {
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k, up = vecNormalize(p.surfaceDir);
  const b = VEHICLE_IMPACT.upBias, along = vecNormalize(vecAdd(vecScale(dir, 1 - b), vecScale(up, b)));
  return { ...p, airborne: true, airbornePos: vecAdd(feetOf(state, p), vecScale(up, 0.05 * person)), airborneVel: vecScale(along, speed), airborneCause: 'struck' };
}

/**
 * The hull at `index` (moved this tick) strikes the people on foot it meets. `driverId`: the unit in its driver's seat,
 * or null. Null when it struck nobody.
 */
export function strikePeople(state: GameState, index: number, driverId: number | null, tick: number): { state: GameState; events: ImpactEvent[] } | null {
  const s = state.structures[index], d = s.drive;
  const M = hullMass(s.type);
  if (!d || M <= 0) return null;
  const ps = personSetOf(state), walk = ps.walkSpeed, r = ps.unitRadius;
  const speed = Math.abs(d.speed);
  if (speed < VEHICLE_IMPACT.minWalks * walk) return null;
  const planet = state.planets[s.planetIndex];
  const n = vecNormalize(s.surfaceDir);
  const heading = vecNormalize(projectToTangent(d.heading, n));
  const vel = vecScale(heading, d.speed);
  const shape = drivenHullShape(state, s);
  const reach = shape.reach + 2 * r;
  const driver = driverId === null ? undefined : state.players.find(p => p.id === driverId);
  const team = driver?.teamIndex ?? state.players.find(p => p.id === s.ownerId)?.teamIndex;
  const share = friendlyShare(state.config);
  const tracked = s.type === 'tracked' || s.type === 'tank';
  const pairTicks = Math.round(VEHICLE_IMPACT.pairSeconds / PHYS_DT);
  let players: PlayerState[] | null = null, lost = 0, credit = 0;
  const events: ImpactEvent[] = [];
  for (let k = 0; k < state.players.length; k++) {
    const p = state.players[k];
    if (!p.alive || p.planetIndex !== s.planetIndex || p.mount || p.boarded || p.airborne || p.airbornePos || p.rampWalk || p.piloting !== undefined) continue;
    const pd = vecNormalize(p.surfaceDir);
    if (vecLength(vecSub(pd, n)) * planet.radius > reach) continue;
    if (p.struck && p.struck.hull === s.id && tick - p.struck.tick < pairTicks) continue;
    const hip = personHip(planet, pd, ps);
    if (shape.bound && primDistance(hip, shape.bound) > r) continue;
    let best = Infinity, part: Prim | null = null;
    for (const q of shape.prims) { const dd = primDistance(hip, q); if (dd < best) { best = dd; part = q; } }
    if (!part || best > r) continue;
    // the contact's normal across the ground: from the part's nearest point to the hip — from the hull's middle when inside it
    let normal = projectToTangent(best > 1e-9 ? vecSub(hip, primNearest(hip, part)) : vecSub(pd, n), pd);
    normal = vecLength(normal) > 1e-12 ? vecNormalize(normal) : vecScale(heading, d.speed >= 0 ? 1 : -1);
    const c = vecDot(vel, normal);
    if (c < VEHICLE_IMPACT.minWalks * walk) continue;
    const friend = team !== undefined && p.teamIndex === team;
    const scale = friend ? share : 1;
    if (scale <= 0) continue;   // (friendly fire off: shoved, unhurt, as before)
    const dv = impactDv(c, M);
    lost = Math.max(lost, dv.hull);
    const dvw = dv.person / walk, prone = p.stance === 'prone';
    const crushed = prone && tracked && c >= VEHICLE_IMPACT.crushWalks * walk;
    const amount = (crushed ? p.health : BIKE_DRIVE.crashDamage * dvw * dvw) * scale;
    const hit = damageUnit(p, amount, 'impact', driverId ?? undefined);
    const throws = !hit.killed && !prone && dvw > VEHICLE_IMPACT.throwWalks;
    let u: PlayerState = { ...hit.unit, struck: { hull: s.id, tick } };
    if (throws) u = thrown(state, u, normal, dv.person);
    const credited = hit.killed && driver !== undefined && !friend;
    if (credited) credit++;
    (players ??= state.players.slice())[k] = u;
    events.push({ kind: 'struck', structureId: s.id, unitId: p.id, driverId, dv: dvw, damage: hit.dealt, thrown: throws, crushed, killed: hit.killed, ...(credited ? { killerId: driverId! } : {}) });
  }
  if (!players) return null;
  if (credit > 0 && driver) { const di = players.findIndex(p => p.id === driver.id); players[di] = { ...players[di], kills: players[di].kills + credit }; }
  // the hull's share of the blow off its speed
  const left = Math.max(0, speed - lost);
  const structures = state.structures.slice();
  structures[index] = { ...s, drive: { ...d, speed: d.speed >= 0 ? left : -left } };
  let out: GameState = { ...state, players, structures };
  // a bike that loses enough throws its rider: off the bike, hurt by the crash rule × the rider's share on its speed
  if (s.type === 'motorcycle' && driver && lost > VEHICLE_IMPACT.riderThrowWalks * walk) {
    out = dismountUnit(out, driver.id, 0);
    const ri = out.players.findIndex(p => p.id === driver.id);
    const damage = VEHICLE_IMPACT.riderShare * BIKE_DRIVE.crashDamage * (speed / walk) * (speed / walk);
    const hit = damageUnit(out.players[ri], damage, 'impact');
    const rider = hit.killed ? hit.unit : thrown(out, hit.unit, vecScale(heading, d.speed >= 0 ? 1 : -1), speed);
    out = { ...out, players: out.players.map((p, k) => (k === ri ? rider : p)) };
    events.push({ kind: 'bikeCrash', structureId: s.id, unitId: driver.id, speed, damage: hit.dealt });
  }
  return { state: out, events };
}
